/**
 * Custom Web Component for an address bar that provides location autocomplete,
 * integrates with Mapbox geocoding, and displays suggestions.
 *
 * @remarks
 * This class manages map-related search input, suggestions, localization (i18n),
 * geocoding requests, and user interactions in a shadow DOM context. It can be configured via
 * attributes and dispatches events for autolocation & location selection.
 *
 * @example
 * <awe-address-bar lang="en" limit="5" countries="US,CA"></awe-address-bar>
 *
 * @public
 */
class AweAddressBar extends HTMLElement {
    get lang() {
        return this._lang;
    }
    set lang(value) {
        this._lang = value.substring(0, 2);
        this.setAttribute('lang', this._lang);
    }
    get places() {
        return this._places;
    }
    set places(value) {
        this._places = value;
        this._updateList(this._places);
    }
    constructor() {
        super();
        this._lang = 'en';
        this.limit = 5; // max number of results
        this.countries = [];
        this._places = []; // list of location objects, current suggestions
        this._selectedIdx = -1; // selection index when keyboard-navigating suggestions
        this._query = ''; // stores user input before overwriting with suggested results
        this._throttleId = -1; // debouncer id to throttle mapbox requests
        this._address = ''; // displayed address
        this.attachShadow({ mode: 'open' });
        const children = [];
        const style = document.createElement('style');
        children.push(style);
        style.textContent = `
      :host{
        display: flex;
        position:relative;
        width: 100%;
        overflow: hidden;
        color: black;
        border: none;
        overflow: visible;

        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: center;
        padding: 5px 15px 5px 15px;

        --text-size: 1em;
      }
      input[type="text"]{
        flex: 1 1;
        font-family: var(--font-default, Arial);
        font-size: var(--text-size);
        border: none;
        padding: 0 10px;
        margin: 0 5px;
        width: 100%;
        text-overflow: ellipsis;
      }
      *:focus{
        outline: 1px var(--main-color, #000) dotted;
      }

      *, *:before, *:after{
        box-sizing: border-box;
      }

      button{
        background: none;
        border: none;
        flex: 0 0;
        padding: 0;
        cursor: pointer;
      }
      .locate{
        height: 16px;
      }
      svg{
        fill: var(--main-color, #000);
      }

      #places {
        z-index: 500;
        position: absolute;
        background: white;
        border: 1px solid black;
        border-top: none;
        margin: 0;
        padding: 0;
        top: 100%;

        list-style-type: none;
        cursor: default;

        font-size: var(--text-size);
      }
      #places:empty{
        display: none;
      }
      li{
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding: 2px 0 2px 10px;
      }
      li:not(:last-child){
        border-bottom: 1px solid grey;
      }
      li.selected{
        background: black;
        color: white;
      }
    `;
        const pictoSlot = document.createElement('slot');
        pictoSlot.name = 'picto';
        pictoSlot.innerHTML = '<svg id="map-icon" width="18" height="18" role="presentation" viewBox="0 0 1000 1000" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"><path d="m33.375 919.6 219.9-219.9c-65-73.4-105.8-168.7-105.8-274.2.1-229.1 186.5-415.5 415.6-415.5 229.1 0 415.6 186.4 415.6 415.5S792.175 841 563.075 841c-93.9 0-179.6-32.5-249.3-85.1l-222 222.1c-8.1 8.1-18.6 12.1-29.2 12.1-10.6 0-21.1-4-29.2-12.1-16.1-16.2-16.1-42.3 0-58.4zm862.7-494.1c0-183.6-149.4-333-333-333-183.7 0-333 149.4-333 333s149.4 332.9 333 332.9 333-149.3 333-332.9z"/></svg>';
        children.push(pictoSlot);
        const input = document.createElement('input');
        children.push(input);
        input.type = 'text';
        input.id = 'search-input';
        input.placeholder = i18n.get('AWE-ADDRESS-BAR.ADDRESS_PLACEHOLDER');
        input.autocomplete = 'off';
        input.role = 'searchbox';
        input.setAttribute('aria-label', i18n.get('AWE-ADDRESS-BAR.SEARCH_LBL'));
        const listNd = document.createElement('ol');
        listNd.id = 'places';
        children.push(listNd);
        const locNd = document.createElement('button');
        locNd.setAttribute('aria-label', i18n.get('AWE-ADDRESS-BAR.AUTOLOC_LBL'));
        children.push(locNd);
        locNd.classList.add('locate');
        locNd.innerHTML = `
      <slot name="locate"><svg version="1.1" id="locate-icon" width="18" height="18" role="presentation" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 1000 1000" xml:space="preserve"><path d="M 466.76 11.75 c -0.93 0.7 -1.63 25.89 -1.63 55.99 v 54.35 h -6.07 c -10.73 0 -52.72 9.33 -74.42 16.56 c -57.15 19.13 -104.28 48.99 -150.7 95.18 c -46.42 46.42 -76.05 93.31 -95.18 151.16 c -7.7 22.63 -16.56 62.05 -16.56 73.25 v 6.77 l -55.29 0.47 l -55.52 0.7 l -0.7 68.81 l 55.52 0.47 l 55.52 0.7 l 0.7 7 c 1.4 16.1 10.27 54.12 16.8 74.18 c 19.36 57.85 48.99 104.74 95.41 151.16 c 46.42 46.19 93.54 76.05 150.7 95.18 c 21.69 7.23 63.68 16.56 74.42 16.56 h 6.07 v 109.64 h 69.98 l 0.47 -54.12 l 0.7 -53.89 l 23.33 -3.97 c 78.15 -13.3 146.73 -48.29 205.05 -104.74 c 61.35 -59.49 99.84 -132.03 113.14 -213.68 l 3.96 -23.33 l 54.12 -0.7 l 54.12 -0.47 v -69.97 H 880.83 l -1.4 -13.53 c -4.67 -40.12 -25.89 -100.54 -48.99 -139.03 c -59.95 -100.55 -158.4 -168.9 -270.84 -188.03 l -23.33 -3.97 l -0.7 -55.05 l -0.47 -55.29 h -33.59 c -18.19 0.01 -34.05 0.71 -34.75 1.64 z m 83.51 181.49 c 123.87 18.43 220.21 104.28 252.17 225.11 c 20.76 78.15 9.1 171.23 -29.86 239.34 c -45.26 79.32 -117.81 131.1 -209.02 149.53 c -29.16 5.83 -98.91 5.83 -126.44 -0.23 c -97.98 -21.23 -172.63 -78.15 -214.62 -163.53 c -22.63 -45.96 -32.66 -89.34 -32.66 -142.3 c 0 -52.95 10.03 -96.34 32.66 -142.3 c 46.19 -93.78 131.8 -153.26 241.44 -167.49 c 17.98 -2.33 64.64 -1.4 86.33 1.87 z"/><path d="M470.96 324.11c-73.25 15.4-128.3 67.42-144.63 136.7-1.87 8.4-3.5 25.89-3.5 40.36 0 49.22 15.4 88.41 48.52 123.17 34.06 35.46 71.15 52.25 120.84 54.59 36.16 1.63 56.45-2.57 88.41-17.96 98.68-47.59 129.94-174.26 65.55-264.54-24.49-34.53-64.15-60.65-105.67-69.98-15.4-3.51-57.16-4.67-69.52-2.34z"/></svg>
      </slot>`;
        const tmpl = document.createElement('template');
        tmpl.id = 'row-tmpl';
        tmpl.innerHTML = '<li tabindex="0"></li>';
        children.push(tmpl);
        this.shadowRoot.append(...children);
        input.addEventListener('keydown', this._onKeyDown.bind(this));
        input.addEventListener('input', this._onInput.bind(this));
        input.addEventListener('focus', (e) => {
            e.currentTarget.value = '';
        });
        input.addEventListener('blur', (e) => {
            e.currentTarget.value = this._address;
        });
        document.addEventListener('location-update', evt => {
            const e = evt;
            this._address = e.detail.location.address || '';
            this.shadowRoot.getElementById('search-input').value = this._address;
        });
        locNd.addEventListener('click', () => {
            this.dispatchEvent(new CustomEvent('autoloc', { bubbles: true, detail: { auto: false } }));
        });
    }
    connectedCallback() {
        if (!this.isConnected)
            return;
        if (this.hasAttribute('lang')) {
            this.lang = this.getAttribute('lang');
        }
        if (this.hasAttribute('limit')) {
            const attr = this.getAttribute('limit');
            const value = parseInt(attr, 10);
            if (!isNaN(value))
                this.limit = value;
        }
        if (this.hasAttribute('countries'))
            this.countries = this.getAttribute('countries').split(',').map(x => x.trim()).filter(x => !!x);
    }
    /**
     * Handles user input and updates autocomplete suggestions in the address bar.
     */
    _onInput(evt) {
        evt.stopPropagation();
        if (this._throttleId != -1)
            clearTimeout(this._throttleId);
        const query = evt.currentTarget.value;
        this._throttleId = setTimeout(() => {
            this._updateSuggestions(query);
        }, 200);
    }
    /**
     * Sends a geocoding request to the Mapbox API based on the provided query string,
     * and updates the list of place suggestions in the address bar. Updates the component's
     * `places` property with the response results. The request is only sent if the query
     * length is at least 3 characters.
     *
     * @param query - The search string entered by the user.
     * @returns A Promise that resolves when the suggestions have been updated.
     *
     * @remarks
     * This method handles errors gracefully by logging them and returning an empty features object.
     * After the request is completed (even on error), it logs the Mapbox API usage for tracking purposes.
     */
    async _updateSuggestions(query) {
        if (query.length < 3)
            return;
        try {
            this._query = query;
            let qs = `language=${this.lang}&limit=${this.limit}&access_token=pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA`;
            qs += '&country=' + this.countries.join(',');
            const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?${qs}`);
            const ac = await res.json();
            this.places = ac.features.map((x) => player.parseFeature(x));
        }
        catch (err) {
            console.error(err); // eslint-disable-line no-console
        }
        finally {
            // track mapbox usage
            tracker.track('mapbox-request', { api: 'geocode', query }, TARGET_LOG);
        }
    }
    /**
     * Updates the DOM nodes in the suggestion list to reflect the current set of places.
     *
     * This method synchronizes the list of suggestion nodes within the shadow DOM's
     * "places" container to match the provided array of `places`. Existing nodes are updated
     * with new values, excess nodes are removed, and missing nodes are appended and set up
     * with necessary event listeners.
     *
     * @param places - An array of `Location` objects representing the suggestions to display.
     *
     * @remarks
     * - This updates each list item (`li`) with the place address and proper dataset.
     * - Click and keyboard event listeners are attached to newly created list nodes.
     * - The method also updates the width and left position of the "places" container to align
     *   with the search input's bounding rectangle.
     * - After updating, the current selection index is reset to -1.
     */
    _updateList(places) {
        const parent = this.shadowRoot.getElementById('places');
        const tmpl = this.shadowRoot.getElementById('row-tmpl').content.firstElementChild;
        const input = this.shadowRoot.getElementById('search-input');
        const rect = input.getBoundingClientRect();
        parent.style.width = `${rect.width}px`;
        parent.style.left = `${input.offsetLeft}px`;
        const nds = this.shadowRoot.querySelectorAll('li');
        let curIdx = -1;
        // update existing nodes with new values & remove excess nodes
        for (let i = 0; i < nds.length; i++) {
            curIdx = i;
            if (places.length > i) {
                nds[i].textContent = places[i].address;
                nds[i].classList.remove(('selected'));
            }
            else {
                nds[i].remove();
            }
        }
        // add missing nodes
        if (places.length > nds.length) {
            for (let i = curIdx + 1; i < places.length; i++) {
                const nd = tmpl.cloneNode(true);
                nd.textContent = places[i].address;
                nd.dataset.idx = i.toString();
                nd.addEventListener('click', this._selectPlace.bind(this));
                nd.addEventListener('keydown', this._selectPlace.bind(this));
                parent.appendChild(nd);
            }
        }
        this._selectedIdx = -1;
    }
    /**
     * Handles selection of a place either by click or keyboard interaction.
     *
     * @param evt - The event that triggered the selection (MouseEvent, KeyboardEvent, or null).
     *   If provided, determines the selection method (e.g. click, "Enter" key).
     * @param place - The place object to select. If not provided, it will be determined
     *   from the event's current target (using the dataset index).
     *
     * @remarks
     * - Dispatches a "location-update" event with detail object `{ location: place, auto: false }` if selection is valid.
     * - If called via keyboard, only proceeds for "Enter" key; otherwise, exits early.
     * - If no valid place found, logs a warning to the console.
     * - Resets the places list after selection.
     */
    _selectPlace(evt, place) {
        if (evt && !place) {
            evt.stopPropagation();
            if (evt instanceof KeyboardEvent && evt.key !== 'Enter')
                return;
            const idx = evt.currentTarget.dataset.idx;
            place = this.places[parseInt(idx, 10)];
        }
        if (place)
            this.dispatchEvent(new CustomEvent('location-update', { detail: { location: place, auto: false } }));
        else
            console.warn('no result at index'); // eslint-disable-line no-console
        this.places = [];
    }
    /**
     * Handles keyboard navigation in the suggestion results list.
     *
     * Supports navigating through address suggestions using ArrowUp and ArrowDown keys,
     * reverting to the original user query with Escape, and selecting a suggestion
     * with Enter. The currently selected suggestion is visually highlighted.
     *
     * @remarks
     * - ArrowUp/ArrowDown: Move the selection up or down in the suggestions.
     * - Escape: Closes the suggestion list and restores the user's input.
     * - Enter: Selects the current suggestion (or first one, if none selected).
     */
    _onKeyDown(evt) {
        evt.stopPropagation();
        const list = this.shadowRoot.getElementById('places');
        const target = evt.currentTarget;
        switch (evt.key) {
            case 'ArrowUp':
                if (this._selectedIdx > 0) {
                    list.children[this._selectedIdx].classList.remove('selected');
                    this._selectedIdx--;
                    target.value = this.places[this._selectedIdx].address;
                    list.children[this._selectedIdx].classList.add('selected');
                }
                else if (this._selectedIdx == 0) {
                    this._selectedIdx--;
                    target.value = this._query;
                }
                evt.preventDefault();
                break;
            case 'ArrowDown': // arrow Down
                if (this._selectedIdx < this.places.length - 1) {
                    if (this._selectedIdx >= 0)
                        list.children[this._selectedIdx].classList.remove('selected');
                    this._selectedIdx++;
                    target.value = this.places[this._selectedIdx].address;
                    list.children[this._selectedIdx].classList.add('selected');
                }
                evt.preventDefault();
                break;
            case 'Escape':
                // empty list to hide it, reverts textbox to user input
                this.places = [];
                target.value = this._query;
                break;
            case 'Enter': {
                if (this._selectedIdx === -1)
                    this._selectedIdx = 0;
                const selected = this.places[this._selectedIdx];
                if (!selected) {
                    // if no places to select, set flag to select a place when places array is updated
                    // this.waitForPlaces = true
                    return;
                }
                this._selectPlace(null, selected);
                break;
            }
        }
    }
}
customElements.define('awe-address-bar', AweAddressBar);
// default event handlers for the element
const locbarNd = document.getElementsByTagName('awe-address-bar')[0];
if (locbarNd) {
    locbarNd.addEventListener('autoloc', evt => {
        const e = evt;
        player.autoloc();
        tracker.track('geoloc', { auto: !!e.detail.auto });
    });
    locbarNd.addEventListener('location-update', evt => {
        const e = evt;
        player.store.location = e.detail.location;
        tracker.track('geoloc', { auto: !!e.detail.auto });
    });
}
